import * as React from "react"
import { Modal, Form, Button, Col } from "react-bootstrap"
import { concat, of, Subject, Subscription } from "rxjs"
import { debounceTime, filter, map, switchMap } from "rxjs/operators"
import { toast } from "react-toastify"
import { connect } from "react-redux"
import { FaAngleDown } from "react-icons/fa"
import { EmailRegexp, PhoneRegexp, SiteDomain } from "../../../config/Constants"
import { AsyncTypeahead } from "react-bootstrap-typeahead"
import MaskedInput from "react-maskedinput"
import ActionButton from "../../ActionButton"
import { EmailCheck } from "../../../api/Invitation/InformationGet"
import { GetRoles } from "../../../api/Roles/UserRolesGet"
import Role from "../../../api/Roles/Model/Role"
import ImplementationSearchResult from "../../../api/Invitation/Model/ImplementationSearchResult"
import { AllImplementationsGet } from "../../../api/Invitation/InformationGet"
import { PortalInvitation } from "../../../api/Invitation/InvitationCreate"
import { APIError } from "../../../api/ObservableFromFetch"
import { AppState } from "../../../AppState"
import { getUserNameForSelectedCompany, RoleIdForSelectedCompany } from "../../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import FormatError from "../../../formatters/FormatError"

interface AddNewAdminModalProps {
  show: boolean
  onClose: (created: boolean) => void
}

interface AddNewAdminAuthProps {
  PrimaryRoleID?: number
  UserName?: string
}

interface AddNewAdminModalValidity {
  implementationValid: boolean
  emailValid: boolean
  phoneValid: boolean
  roleValid: boolean
  firstNameValid: boolean
  lastNameValid: boolean
}

export interface AddNewAdminModalData {
  implementationID?: number
  email: string
  phone: string
  firstName: string
  lastName: string
  roleID?: number
}

interface AddNewAdminModalState extends AddNewAdminModalData, AddNewAdminModalValidity {
  emailChecking: boolean
  emailAvailable?: boolean
  saving: boolean
  rolesLoading: boolean
  roles?: Role[]
  implementationsLoading: boolean
  implementations: ImplementationSearchResult[]
  selectedImplementation?: ImplementationSearchResult
}

export class AddNewAdminModal extends React.PureComponent<AddNewAdminModalProps & AddNewAdminAuthProps, AddNewAdminModalState> {
  emptyData: AddNewAdminModalData = {
    implementationID: undefined,
    email: "",
    phone: "",
    firstName: "",
    lastName: "",
    roleID: undefined
  }
  positiveValidity: AddNewAdminModalValidity = {
    implementationValid: true,
    emailValid: true,
    phoneValid: true,
    roleValid: true,
    firstNameValid: true,
    lastNameValid: true
  }
  defaultState: AddNewAdminModalState = {
    ...this.emptyData,
    ...this.positiveValidity,
    emailChecking: false,
    emailAvailable: undefined,
    rolesLoading: false,
    saving: false,
    implementationsLoading: false,
    implementations: []
  }
  state: AddNewAdminModalState = { ...this.defaultState }

  checkEmail$ = new Subject<string>()
  emailExistsSubscription?: Subscription
  userRolesSubscription?: Subscription
  saveSubscription?: Subscription
  implementationsSubscription?: Subscription

  searchImplementation$ = new Subject<string>()
  typeahead = React.createRef<AsyncTypeahead<ImplementationSearchResult>>()

  componentDidMount() {
    this.emailExistsSubscription = this.checkEmail$.pipe(
      map(name => name.trim()),
      filter(name => this.isEmailValid(name)),
      switchMap(name => concat(
        of({ loading: true }),
        EmailCheck(name).pipe(
          map(result => ({ loading: false, result: result.IsEmailExists }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, result?: boolean }) => this.setState({ emailChecking: o.loading, emailAvailable: !o.result }),
      (error) => this.handleAPIError(error, { emailChecking: false, emailAvailable: undefined })
    )

    //TODO: replace with correct RoleID
    const roleID = this.props.PrimaryRoleID || 0
    this.setState({ rolesLoading: true })
    this.userRolesSubscription = GetRoles(roleID).subscribe(
      (data: Role[]) => this.setState({ roles: data, roleID: data.length === 1 ? data[0].RoleID : undefined, rolesLoading: false }),
      (error) => this.handleAPIError(error, { rolesLoading: false, emailAvailable: undefined })
    )

    this.implementationsSubscription = this.searchImplementation$.pipe(
      filter(filter => filter.length >= 3),
      debounceTime(500),
      map(filter => (filter ?? "").trim()),
      switchMap((filter) => concat(
        of({ isLoading: true }),
        AllImplementationsGet(filter).pipe(
          map((data) => ({ isLoading: false, items: data }))
        )
      ))
    ).subscribe(
      (state: { isLoading: boolean, items?: ImplementationSearchResult[] }) => {
        state.items
          ? this.setState({ implementationsLoading: state.isLoading, implementations: state.items })
          : this.setState({ implementationsLoading: state.isLoading })
      },
      (error) => this.handleAPIError(error, { isImplementationsLoading: false })
    )

  }

  componentWillUnmount() {
    this.emailExistsSubscription && this.emailExistsSubscription.unsubscribe()
    this.userRolesSubscription && this.userRolesSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.implementationsSubscription && this.implementationsSubscription.unsubscribe()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error(FormatError(error))
  }

  isEmailValid = (value: string) => EmailRegexp.test(value)
  isPhoneValid = (value: string) => PhoneRegexp.test(value)
  isRoleValid = (value: number | undefined): boolean => !!value && !!this.state.roles?.find(item => item.RoleID === value)
  isNameValid = (value: string) => !!value.trim()

  handleImplementationChange = (items: ImplementationSearchResult[]) => {
    this.setState({
      selectedImplementation: items[0],
      implementationID: items[0]?.ImplementationID || 0,
      implementationValid: !!items[0],
    })
  }

  handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState({ email: value, emailValid: this.isEmailValid(value), emailAvailable: undefined })
  }

  handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState({ phone: value, phoneValid: this.isPhoneValid(value) })
  }

  handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value)
    this.setState({ roleID: value, roleValid: this.isRoleValid(value) })
  }

  handleClose = (hasChanges: boolean) => {
    const { roles } = this.state
    this.setState({ ...this.defaultState, roleID: roles && roles.length === 1 ? roles[0].RoleID : undefined })
    this.props.onClose(hasChanges)
  }

  getPortalSetupModalValidity = () =>{
    const { implementationID, email, emailAvailable, phone, roleID, lastName, firstName } = this.state
    const validity: AddNewAdminModalValidity = {
      implementationValid: !!implementationID,
      emailValid: this.isEmailValid(email) && !!emailAvailable,
      phoneValid: this.isPhoneValid(phone),
      roleValid: this.isRoleValid(roleID),
      firstNameValid: this.isNameValid(firstName),
      lastNameValid: this.isNameValid(lastName)
    }
    return validity
  }

  prepareUrl = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, "").toLowerCase()

  handleCreateClick = () => {
    if (this.state.emailChecking || this.state.rolesLoading || this.state.implementationsLoading) {
      return
    }
    const { implementationID, email, emailAvailable, phone, roleID, selectedImplementation, firstName, lastName } = this.state
    const validity = this.getPortalSetupModalValidity()
    const isValid = Object.values(validity).every((valid: boolean) => valid)
    if (!isValid) {
      this.setState(validity)
      return
    }

    const name = selectedImplementation?.ImplementationName || ""
    const subdomain = selectedImplementation?.SubDomain.split('.')[0] || ""
    const unmaskDonorID = selectedImplementation?.UnmaskDonorID || 0

    this.setState({ saving: true })
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.saveSubscription = PortalInvitation({
      CompanyName: name,
      EmailAddress: email,
      Link: `${this.prepareUrl(subdomain)}.${SiteDomain}`,
      ModifiedBy: this.props.UserName || "",
      PhoneNumber: phone.replace(/-/g, ""),
      RoleID: roleID || 0,
      IsExistingUser: !emailAvailable,
      ImplementationID: implementationID || 0,
      SubscriptionPlanID: 0,
      IsGOVTPortal: false,
      LastName: lastName,
      FirstName: firstName,
      UnmaskDonorID:unmaskDonorID
    }).subscribe(
      (result) => {
        this.setState({ saving: false })
        toast.success(result)
        this.handleClose(true)
      },
      (error) => this.handleAPIError(error, { saving: false })
    )
  }

  handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState({ firstName: value, firstNameValid: this.isNameValid(value) })
  }

  handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState({ lastName: value, lastNameValid: this.isNameValid(value) })
  }

  render() {
    const { implementationValid, email, emailValid, emailChecking, emailAvailable, 
      phone, phoneValid,
      roleID, roleValid, rolesLoading,
      firstName, firstNameValid, lastName, lastNameValid } = this.state
    let disableCreate = emailChecking || rolesLoading

    if (!disableCreate){
        const validity = this.getPortalSetupModalValidity()
        const isValid = Object.values(validity).every((valid: boolean) => valid)
        disableCreate = !isValid
      }
    return (<>
      <Modal centered backdrop="static" size="lg" show={this.props.show} onHide={this.props.onClose}>
        <Modal.Header closeButton>
          <Modal.Title>ADD NEW PORTAL ADMIN</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Row className="form-group required">
              <Form.Label column sm={4} htmlFor="name">Portal Name</Form.Label>
              <Col sm={8}>
                <AsyncTypeahead id="implementation"
                  ref={this.typeahead}
                  labelKey="ImplementationName"
                  filterBy={() => true}
                  isLoading={this.state.implementationsLoading}
                  onSearch={() => { }}
                  onInputChange={input => this.searchImplementation$.next(input)}
                  onChange={this.handleImplementationChange}
                  options={this.state.implementations}
                  selected={this.state.selectedImplementation ? [this.state.selectedImplementation] : []}
                  minLength={3}
                  emptyLabel="No Portals Found"
                  placeholder=""
                  isInvalid={!implementationValid}
                  inputProps={{ autoComplete: "chrome-off" }}>
                  {!this.state.implementationsLoading &&
                    <Button variant="toggle" onClick={() => { this.typeahead.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>}
                </AsyncTypeahead>
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={4} htmlFor="email">Portal Admin Email Address</Form.Label>
              <Col sm={8}>
                <Form.Control id="email"
                  type="text"
                  value={email}
                  onChange={this.handleEmailChange}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => this.checkEmail$.next(e.target.value)}
                  isInvalid={!emailValid}
                  disabled={emailChecking} />
                {!emailChecking && !emailValid && <Form.Control.Feedback type="invalid" className="d-block">Please enter valid email</Form.Control.Feedback>}
                {!emailChecking && emailAvailable && < Form.Control.Feedback type="valid" className="d-block">Email is available</Form.Control.Feedback>}
                {!emailChecking && emailAvailable === false && <Form.Control.Feedback type="invalid" className="d-block">Email is already in use</Form.Control.Feedback>}
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={4} htmlFor="phone">Portal Admin Personal Phone</Form.Label>
              <Col sm={8}>
                <Form.Control id="phone" type="text" as={MaskedInput} mask="111-111-1111" value={phone} onChange={this.handlePhoneChange} isInvalid={!phoneValid} />
                <Form.Text>This phone number will be used to validate the Administrator. Please provide a direct number or mobile number (not a main company number or extension)</Form.Text>
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={4} htmlFor="role">Portal Admin First Name</Form.Label>
              <Col sm={8}>
                <Form.Control id="firstName"
                              type="text"
                              maxLength={20}
                              value={firstName}
                              onChange={this.handleFirstNameChange}
                              onBlur={this.handleFirstNameChange}
                              isInvalid={!firstNameValid}/>
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={4} htmlFor="role">Portal Admin Last Name</Form.Label>
              <Col sm={8}>
                <Form.Control id="lastName"
                              type="text"
                              maxLength={20} 
                              value={lastName}
                              onChange={this.handleLastNameChange}
                              onBlur={this.handleLastNameChange}
                              isInvalid={!lastNameValid}/>
              </Col>
            </Form.Row>
            <Form.Row className="form-group required">
              <Form.Label column sm={4} htmlFor="role">User's Role</Form.Label>
              <Col sm={8}>
                <Form.Control id="role" as="select" onChange={this.handleRoleChange} value={roleID || ""} isInvalid={!roleValid}>
                  {this.state.roles && this.state.roles.length > 1 && <option value="">Select Role</option>}
                  {this.state.roles?.map(role => <option value={role.RoleID} key={role.RoleID}>{role.Title}</option>)}
                </Form.Control>
              </Col>
            </Form.Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.handleClose(false)}>Cancel</Button>
          <ActionButton variant="primary" disabled={disableCreate} onClick={this.handleCreateClick} spin={this.state.saving} disableOnSpin>Create</ActionButton>
        </Modal.Footer>
      </Modal>
    </>
    )
  }
}

function mapStateToProps(state: AppState): AddNewAdminAuthProps {
  return {
    PrimaryRoleID: RoleIdForSelectedCompany(state),
    UserName: getUserNameForSelectedCompany(state)
  }
}

const ConnectedPortalSetupModal = connect(mapStateToProps, null)(AddNewAdminModal)
export default ConnectedPortalSetupModal
